import dayjs from 'dayjs'
import React, { useEffect, useRef, useState } from 'react'
import { NumberParam, useQueryParam } from 'use-query-params'

import { Head } from './components/Head'
import { Share } from './components/Share'
import { ToggleDarkMode } from './components/ToggleDarkMode'
import { SITE_TITLE } from './lib/constants'

function App() {
  const [unixtime, setUnixtime] = useState<number | null>(null)
  const [datetime, setDatetime] = useState('')
  const focusInput = useRef<HTMLInputElement>(null)
  const [unixtimeQueryParam, setUnixtimeQueryParam] = useQueryParam('u', NumberParam)

  useEffect(() => {
    focusInput.current?.focus()
  }, [])

  useEffect(() => {
    if (unixtimeQueryParam) {
      setUnixtime(unixtimeQueryParam)
      convertUnixtimeToDayjs(unixtimeQueryParam)
    }
  }, [unixtimeQueryParam])

  const handleChangeUnixtime = (event: React.ChangeEvent<HTMLInputElement>) => {
    let unixtime = Number(event.target.value)
    if (!unixtime) {
      setUnixtime(null)
      return
    }

    if (unixtime.toString().length === 13) {
      unixtime = convertUnixtimeFromMsecToSec(unixtime)
    }
    setUnixtime(unixtime)
    setUnixtimeQueryParam(unixtime)

    if (unixtime.toString().length >= 10) {
      convertUnixtimeToDayjs(unixtime)
    }
  }

  const handleChangeDateTime = (event: React.ChangeEvent<HTMLInputElement>) => {
    const validate = (date: string, format: string) => {
      return dayjs(date, format).format(format) === date
    }

    const value = event.target.value
    setDatetime(value)
    if (validate(value, 'YYYY-MM-DDTHH:mm:ssZ')) {
      setUnixtime(dayjs(value).unix())
    }
  }

  const handleSetCurrentUnixtime = () => {
    const unixtime = convertUnixtimeFromMsecToSec(Date.now())
    setUnixtime(unixtime)
    setUnixtimeQueryParam(unixtime)
    convertUnixtimeToDayjs(unixtime)
  }

  const convertUnixtimeFromMsecToSec = (unixtime: number) => {
    return Math.floor(unixtime / 1000)
  }

  const convertUnixtimeToDayjs = (unixtime: number) => {
    const day = dayjs.unix(unixtime)
    const currentDateTime = day ? day.format('YYYY-MM-DDTHH:mm:ssZ') : ''
    setDatetime(currentDateTime)
  }

  const sharePath = () => {
    if (unixtimeQueryParam) {
      return `/?u=${unixtime}`
    }
    return '/'
  }

  const shareText = () => {
    if (unixtimeQueryParam) {
      return `${unixtime} = ${datetime}`
    }
    return SITE_TITLE
  }

  return (
    <div className="min-h-screen dark:bg-gray-800 dark:text-gray-200">
      <Head path="/" />
      <main>
        <div className="container max-w-screen-lg px-5 mx-auto">
          <div className="max-w-2xl mx-auto">
            <div className="mb-6 text-lg">
              <div className="py-8">
                <h1 className="text-2xl text-center">
                  <div>{SITE_TITLE}</div>
                  <div className="text-sm text-gray-500 dark:text-gray-200">
                    unix timestamp converter
                  </div>
                </h1>
              </div>
              <form className="px-4 mb-4">
                <div className="mb-4">
                  <label
                    className="block mb-1 font-bold text-gray-500 dark:text-gray-200"
                    htmlFor="unixtime"
                  >
                    <div className="flex">
                      <svg
                        className="w-6 h-6 mr-1"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      Unixtime
                    </div>
                  </label>
                  <div className="flex">
                    <input
                      ref={focusInput}
                      id="unixtime"
                      name="unixtime"
                      type="number"
                      className="w-full p-2 mr-4 border rounded dark:border-gray-700 dark:bg-gray-700 focus:outline-none dark:placeholder-gray-400"
                      value={unixtime || ''}
                      placeholder="unixtime, sec or msec"
                      onChange={handleChangeUnixtime}
                      autoComplete="off"
                    />
                    <button
                      type="button"
                      className="px-4 py-2 font-bold text-white bg-gray-800 rounded shadow hover:bg-gray-700 focus:outline-none dark:text-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600"
                      onClick={handleSetCurrentUnixtime}
                    >
                      Current
                    </button>
                  </div>
                </div>
                <div className="my-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-6 h-6 mx-auto text-gray-500 dark:text-gray-200"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
                    />
                  </svg>
                </div>
                <div className="mb-4">
                  <label
                    className="block mb-1 font-bold text-gray-500 dark:text-gray-200"
                    htmlFor="datetime"
                  >
                    <div className="flex">
                      <svg
                        className="w-6 h-6 mr-1"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      DateTime
                    </div>
                  </label>
                  <input
                    id="datetime"
                    name="datetime"
                    type="text"
                    className="w-full p-2 border rounded focus:outline-none dark:border-gray-700 dark:bg-gray-700"
                    onChange={handleChangeDateTime}
                    value={datetime}
                  />
                </div>
              </form>
            </div>
            <div className="mt-12 mb-6 text-lg">
              <Share path={sharePath()} text={shareText()} />
            </div>
            <div className="flex justify-center">
              <ToggleDarkMode />
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}

export default App
